import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowDown, Github, Linkedin, Mail } from 'lucide-react';
import { theme, Button } from '../styles/theme';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.background};
  background-image: radial-gradient(circle at 25% 25%, rgba(58, 123, 213, 0.04) 0%, transparent 50%);
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  text-align: center;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: ${theme.fontSizes['5xl']};
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['4xl']};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing['2xl']};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.75;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes.base};
  }
`;

const HeroActions = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.lg};
  justify-content: center;
  align-items: center;
  margin-bottom: ${theme.spacing['3xl']};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.lg};
  justify-content: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.surface};
  color: ${theme.colors.textLight};
  transition: all 0.2s ease;
  border: 1px solid ${theme.colors.border};

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${theme.spacing['2xl']};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textLight};
  cursor: pointer;
`;

const ScrollText = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const BackgroundDecoration = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const NavigationSection = styled.section`
  padding: ${theme.spacing['3xl']} 0;
  background: ${theme.colors.backgroundAlt};
  border-top: 1px solid ${theme.colors.border};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes['4xl']};
  font-weight: 700;
  text-align: center;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const NavigationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing['2xl']};
  margin-top: ${theme.spacing['2xl']};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
`;

const NavigationCard = styled(motion.a)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing['2xl']};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary};
  }
`;

const CardIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing.lg};
  color: white;
`;

const CardTitle = styled.h3`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
`;

const CardDescription = styled.p`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.textLight};
  text-align: center;
  line-height: 1.6;
`;

export const Home: React.FC = () => {
  const scrollToNavigation = () => {
    const navigationSection = document.getElementById('navigation-section');
    navigationSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://your-domain.vercel.app';

  return (
    <>
      <SEO
        title="Antonio Michal Verdile – Software Engineer"
        description="Software engineer specializing in full-stack web development with expertise in React, TypeScript, C#, and .NET. Proven track record delivering scalable enterprise applications for Swiss National Bank and academic research projects."
        canonical={siteUrl}
      />
      <StructuredData type="home" />
      
      <HeroSection>
        <BackgroundDecoration aria-hidden="true" />
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span style={{ color: theme.colors.primary }}>Antonio Michal Verdile</span>
            <br />
            Full Stack Developer
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Software engineer specializing in full-stack web development with expertise in React, TypeScript, C#, and .NET. 
            Proven track record of delivering scalable enterprise applications and innovative solutions across academic and professional environments.
          </HeroSubtitle>

          <HeroActions
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Button as="a" href="mailto:michaelverdile1@gmail.com" size="lg">
              <Mail size={20} />
              Contact Me
            </Button>
          </HeroActions>

          <SocialLinks
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <SocialLink 
              href="https://github.com/MichalVerdile" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <Github size={24} />
            </SocialLink>
            <SocialLink 
              href="https://linkedin.com/in/michal-verdile-61b813151" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <Linkedin size={24} />
            </SocialLink>
            <SocialLink 
              href="mailto:michaelverdile1@gmail.com"
              aria-label="Email"
            >
              <Mail size={24} />
            </SocialLink>
          </SocialLinks>
        </HeroContent>

        <ScrollIndicator
          onClick={scrollToNavigation}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <ScrollText>View Portfolio</ScrollText>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <ArrowDown size={20} />
          </motion.div>
        </ScrollIndicator>
      </HeroSection>

      <NavigationSection id="navigation-section">
        <SectionContainer>
          <SectionTitle>Explore My Portfolio</SectionTitle>
          <SectionSubtitle>
            Discover my professional background, technical expertise, and portfolio of projects demonstrating full-stack development capabilities.
          </SectionSubtitle>
          
          <NavigationGrid>
            <NavigationCard
              href="/about"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <CardIcon>
                <Mail size={40} />
              </CardIcon>
              <CardTitle>About Me</CardTitle>
              <CardDescription>
                Professional experience, educational background, and technical skills in modern web development and software engineering.
              </CardDescription>
            </NavigationCard>

            <NavigationCard
              href="/projects"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <CardIcon>
                <Github size={40} />
              </CardIcon>
              <CardTitle>Projects</CardTitle>
              <CardDescription>
                Comprehensive portfolio showcasing academic and professional projects with innovative solutions and modern technologies.
              </CardDescription>
            </NavigationCard>

            <NavigationCard
              href="/contact"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <CardIcon>
                <Linkedin size={40} />
              </CardIcon>
              <CardTitle>Contact</CardTitle>
              <CardDescription>
                Connect for professional opportunities, collaboration, or to discuss how I can contribute to your projects.
              </CardDescription>
            </NavigationCard>
          </NavigationGrid>
        </SectionContainer>
      </NavigationSection>
    </>
  );
};